import { getSdk, getApiBaseUrl } from "@/composables/useSdk";
import { useAuthStorage } from "@/composables/useAuthStorage";
import type { AuthUser, AuthTokens } from "@/types";

/**
 * Normalize badges into a string array (API can return strings or objects)
 */
function normalizeBadges(rawBadges: unknown): string[] {
  if (!Array.isArray(rawBadges)) return [];

  return rawBadges
    .map((badge) => {
      if (typeof badge === "string") return badge;
      if (badge && typeof badge === "object" && "name" in badge) {
        return String((badge as { name: unknown }).name);
      }
      return null;
    })
    .filter((badge): badge is string => Boolean(badge));
}

/**
 * Derive a display handle when username is missing (e.g. pre-setup or API gap).
 * Returns non-empty string for @ display; "unknown" only when we have nothing.
 */
function deriveDisplayHandle(
  name: string | undefined,
  email: string | undefined
): string {
  if (name && name.trim()) {
    // Use slug from name: "John Doe" -> "johndoe"
    const slug = name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9-_]/g, "");
    if (slug) return slug;
  }
  if (email && email.includes("@")) {
    const local = email.split("@")[0]?.trim();
    if (local) return local;
  }
  return "unknown";
}

/**
 * Determine admin status using either helper method or badge list
 */
function computeIsAdmin(profile: unknown, badges: string[]): boolean {
  const maybeProfile = profile as { isAdmin?: unknown };

  if (typeof maybeProfile.isAdmin === "function") {
    try {
      return (maybeProfile.isAdmin as () => boolean)();
    } catch (error) {
      console.warn(
        "authService: profile.isAdmin threw, falling back to badge check",
        error,
      );
    }
  }

  return badges.some((badge) => badge.toLowerCase() === "administrator");
}

// Storage keys
const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "auth_user";

// Get auth storage instance
const authStorage = useAuthStorage();

class AuthService {
  /**
   * Send auth code to email (magic link)
   */
  async sendMagicLink(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    const sdk = getSdk();
    await sdk.requestMagicLink(email, {
      referralCode: options?.referralCode || undefined,
      redirect: options?.redirect || undefined,
      platform: options?.platform,
    });
  }

  /**
   * Send auth code to email (alias for sendMagicLink with cleaner name)
   */
  async sendAuthCode(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    const sdk = getSdk();
    await sdk.requestAuthCode(email, {
      referralCode: options?.referralCode || undefined,
      redirect: options?.redirect || undefined,
      platform: options?.platform,
    });
  }

  /**
   * Verify the 6-digit code from magic link
   */
  async verifyCode(
    email: string,
    code: string,
  ): Promise<{ tokens: AuthTokens; user: AuthUser }> {
    const sdk = getSdk();

    // Use SDK to login with magic link
    const sdkTokens = await sdk.loginWithMagicLink(email, code);

    // Convert SDK tokens to our AuthTokens type
    const tokens: AuthTokens = {
      accessToken: sdkTokens.accessToken,
      refreshToken: sdkTokens.refreshToken,
    };

    // Store tokens
    await this.setTokens(tokens);

    // Fetch user profile using SDK
    const user = await this.fetchCurrentUser();

    return { tokens, user };
  }

  /**
   * Login with OAuth provider (Google, Apple, etc.)
   * @param provider - OAuth provider name (e.g., 'google', 'apple')
   * @param code - Authorization code from OAuth callback
   * @param redirectUri - The redirect URI used in the OAuth flow
   * @param extraData - Additional provider-specific data (e.g., Apple's id_token and user info)
   */
  async loginWithOAuth(
    provider: string,
    code: string,
    redirectUri?: string,
    extraData?: { id_token?: string | null; user?: string | null },
  ): Promise<{ tokens: AuthTokens; user: AuthUser }> {
    const sdk = getSdk();

    // Use SDK to exchange OAuth code for tokens
    const sdkTokens = await sdk.loginWithOAuth(provider, code, redirectUri, extraData);

    // Convert SDK tokens to our AuthTokens type
    const tokens: AuthTokens = {
      accessToken: sdkTokens.accessToken,
      refreshToken: sdkTokens.refreshToken,
    };

    // Store tokens
    await this.setTokens(tokens);

    // Fetch user profile using SDK
    const user = await this.fetchCurrentUser();

    return { tokens, user };
  }

  /**
   * Fetch current authenticated user.
   * Uses SDK getCurrentUser (includes roles from API when SDK passes them through).
   */
  async fetchCurrentUser(): Promise<AuthUser> {
    const sdk = getSdk();
    const profile = await sdk.getCurrentUser();

    if (!profile) {
      throw new Error("Failed to fetch user profile");
    }

    const p = profile as Record<string, unknown>;
    const badges = normalizeBadges(p.badges);
    const isAdmin = computeIsAdmin(profile, badges);

    // Username: API returns 'username'; fallback to local part of email or slug of name when missing
    const rawUsername =
      profile.username ??
      (p.username as string | undefined) ??
      (p.user_name as string | undefined);
    const username =
      rawUsername && String(rawUsername).trim()
        ? String(rawUsername).trim()
        : deriveDisplayHandle(
            (p.name as string | undefined) || profile.displayName,
            p.email as string | undefined
          );

    // Map SDK CurrentUser to AuthUser (preserve roles from API)
    const user: AuthUser = {
      ulid: profile.ulid || "",
      username,
      name: (p.name as string | undefined) || profile.displayName || "",
      displayName: profile.displayName || (p.name as string | undefined) || "",
      email: (p.email as string | undefined) || "",
      avatar: (p.avatar || p.avatarUrl || profile.avatarUrl) as string | undefined,
      avatarUrl: profile.avatarUrl,
      avatarVariants: profile.avatarVariants ?? null,
      background: (p.background || p.backgroundImage || profile.backgroundImage) as
        | string
        | undefined,
      backgroundImage:
        typeof profile.backgroundImage === "string"
          ? profile.backgroundImage
          : undefined,
      backgroundVariants: profile.backgroundVariants ?? null,
      bio: profile.bio,
      website: (p.website || p.websiteUrl) as string | undefined,
      followers: profile.followersCount ?? 0,
      following: profile.followingCount ?? 0,
      postCount: profile.postsCount ?? 0,
      verified: p.verified as boolean | undefined,
      // Use badges directly from SDK CurrentUser (already extracted as string[])
      badges,
      isAdmin,
      // Roles from API (crm_user, crm_admin, etc.) - gates CRM menu
      roles: Array.isArray(p.roles) ? (p.roles as string[]) : [],
      // Account setup status
      accountSetup: p.accountSetup as boolean | undefined,
      acceptTermsOfService: p.acceptTermsOfService as boolean | undefined,
      // Referral code for share tracking
      referralCode: p.referralCode as string | undefined,
    };

    // Store user in localStorage
    localStorage.setItem(USER_KEY, JSON.stringify(user));

    return user;
  }

  /**
   * Store user data in localStorage (for read-after-write pattern)
   */
  storeUser(user: AuthUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Set authentication tokens
   * Uses secure storage on native platforms, localStorage on web
   */
  async setTokens(tokens: AuthTokens): Promise<void> {
    const sdk = getSdk();

    if (tokens.accessToken) {
      // Store in secure storage (native) or localStorage (web)
      await authStorage.setAuthToken(tokens.accessToken);
      // Also keep in localStorage for backward compatibility and SDK initialization
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    }
    
    sdk.setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });

    if (tokens.refreshToken) {
      await authStorage.setRefreshToken(tokens.refreshToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
  }

  /**
   * Set developer token directly
   */
  setDeveloperToken(token: string): void {
    const sdk = getSdk();

    localStorage.setItem(TOKEN_KEY, token);
    sdk.setTokens({ accessToken: token });
  }

  /**
   * Get stored tokens (synchronous - for SDK initialization)
   * Reads from localStorage for synchronous access during initialization
   * Secure storage is used for writes, but reads fall back to localStorage for compatibility
   */
  getTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem(TOKEN_KEY);
    if (!accessToken) return null;

    return {
      accessToken,
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || undefined,
    };
  }

  /**
   * Get stored tokens from secure storage (async - for native platforms)
   * Prefers secure storage on native, falls back to localStorage
   */
  async getTokensAsync(): Promise<AuthTokens | null> {
    if (authStorage.isNative) {
      // On native, try secure storage first
      const accessToken = await authStorage.getAuthToken();
      if (!accessToken) return null;
      
      const refreshToken = await authStorage.getRefreshToken();
      return {
        accessToken,
        refreshToken: refreshToken || undefined,
      };
    } else {
      // On web, use localStorage (synchronous)
      return this.getTokens();
    }
  }

  /**
   * Get stored user
   */
  getStoredUser(): AuthUser | null {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get current user (alias for getStoredUser)
   */
  getUser(): AuthUser | null {
    return this.getStoredUser();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return Boolean(localStorage.getItem(TOKEN_KEY));
  }

  /**
   * Refresh access token using refresh token.
   * Used by SDK, crmAuthFetch, and drip authFetch to recover from 401.
   */
  async refreshTokens(): Promise<AuthTokens> {
    const { tokens: currentTokens } = this.initializeFromStorage();

    if (!currentTokens?.refreshToken) {
      throw new Error("No refresh token available");
    }

    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ refresh_token: currentTokens.refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = (await response.json()) as {
      access_token?: string;
      accessToken?: string;
      data?: { access_token?: string };
      refresh_token?: string;
      refreshToken?: string;
    };

    const newAccessToken =
      data.access_token || data.accessToken || data.data?.access_token;
    const newRefreshToken =
      data.refresh_token || data.refreshToken;

    if (!newAccessToken) {
      throw new Error("No access token in refresh response");
    }

    const newTokens: AuthTokens = {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken || currentTokens.refreshToken,
    };

    await this.setTokens(newTokens);
    return newTokens;
  }

  /**
   * Logout - clear all auth data
   */
  async logout(): Promise<void> {
    const sdk = getSdk();

    // Clear from secure storage (native) and localStorage (web)
    await authStorage.removeAuthTokens();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sdk.setTokens(null);
  }

  /**
   * Initialize auth state from storage
   * Called on app startup
   * NOTE: This method must NOT call getSdk() to avoid circular dependency
   * during SDK initialization. The SDK will receive tokens from getInitialTokens().
   */
  initializeFromStorage(): {
    tokens: AuthTokens | null;
    user: AuthUser | null;
  } {
    const tokens = this.getTokens();
    const user = this.getStoredUser();

    // NOTE: We intentionally do NOT call getSdk().setTokens() here
    // because this method is called during SDK initialization.
    // The SDK will set tokens itself after receiving them from getInitialTokens().

    return { tokens, user };
  }
}

// Export singleton
export const authService = new AuthService();
export default authService;
